import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import VoiceCall from "@/components/VoiceCall";
import CoachReady from "@/components/CoachReady";
import { useOrbVoice } from "@/lib/orbVoice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2, ArrowRight, Sparkles, Moon } from "lucide-react";
import TalkWrapCard from "@/components/TalkWrapCard";

const STARTERS = ["I've got a date tonight", "I froze in a conversation", "I want to feel more confident"];
import { useNavigate, useSearchParams } from "react-router-dom";
import { markFromCoach } from "@/components/BackToCoach";
import CoachPlayCard from "@/components/CoachPlayCard";
import CoachLinkCard from "@/components/CoachLinkCard";
import MomentCard from "@/components/MomentCard";
import InviteNudge from "@/components/InviteFriend";
import { toast } from "sonner";

export default function Talk() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [nudge, setNudge] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [opening, setOpening] = useState(null);
  const [historyLen, setHistoryLen] = useState(0);
  const [showEarlier, setShowEarlier] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    Promise.all([api.get("/talk/history"), api.get("/talk/opening", { params: { local_hour: new Date().getHours(), local_date: new Date().toLocaleDateString("en-CA") } })]).then(([h, o]) => {
      const msgs = h.data.messages || [];
      setOpening(o.data.text);
      setHistoryLen(msgs.length);
      setMessages([...msgs, { role: "mirro", text: o.data.text, opening: true }]);
      const back = params.get("back");
      if (back !== null) {
        setParams({}, { replace: true });
        api.post("/talk/return", { tool: back }).then((r) => setMessages((m) => [...m, { role: "mirro", text: r.data.reply }])).catch(() => {});
      }
    }).catch(() => {});
    if (new URLSearchParams(window.location.search).get("call") === "1") setCallOpen(true);
  }, []);
  useEffect(() => {
    if (messages.length <= historyLen + 1 && !showEarlier) return; // arrival: stay on his coach, not the old transcript
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    scrollRef.current?.lastElementChild?.scrollIntoView({ block: "end", behavior: "smooth" });
    const t = setTimeout(() => scrollRef.current?.lastElementChild?.scrollIntoView({ block: "end" }), 400);
    return () => clearTimeout(t);
  }, [messages, sending, historyLen, showEarlier]);
  const live = messages.length > historyLen + 1 || sending;
  const myTurns = messages.slice(historyLen).filter((m) => m.role === "user").length;
  const [wrap, setWrap] = useState(null);
  const [wrapping, setWrapping] = useState(false);
  const callTurns = useRef(0);
  const wrapUp = async (source) => {
    setWrapping(true);
    try {
      const { data } = await api.post("/talk/wrap", { source, local_date: new Date().toLocaleDateString("en-CA") });
      setWrap({ ...data, overlay: source === "call" });
    } catch { if (source === "chat") toast.error("Couldn't wrap up just now."); }
    setWrapping(false);
  };
  const shown = showEarlier ? messages : messages.slice(historyLen);
  const { amplitudeRef, speak, stop } = useOrbVoice(user?.voice || "sage", "talk-greeting");
  const speakingRef = useRef(false);
  const [hearState, setHearState] = useState("idle");
  const hearStop = () => { stop(); speakingRef.current = false; setHearState("idle"); };
  const hear = async () => {
    if (hearState === "speaking") { hearStop(); return; }
    if (hearState === "loading") return;
    setHearState("loading");
    try {
      const { data } = await api.post("/talk/opening/audio", { text: opening });
      speakingRef.current = true; setHearState("speaking");
      await speak(null, data.audio);
    } catch { toast.error("Couldn't play the voice just now."); }
    speakingRef.current = false; setHearState("idle");
  };
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (text, sessionId) => {
    const { data } = await api.post("/talk", { text, session_id: sessionId, local_date: new Date().toLocaleDateString("en-CA"), tz_offset: new Date().getTimezoneOffset() });
    return data.play ? { text: data.reply, play: data.play } : data.reply;
  };

  const welcomeBack = (i) => setMessages((m) => {
    if (i !== m.length - 1) return m;
    api.post("/talk/reset-done").catch(() => {});
    return [...m, { role: "mirro", text: "Welcome back. How do you feel now?" }];
  });

  const submit = async (text) => {
    if (!text.trim() || sending) return;
    setInput(""); setMessages((m) => [...m, { role: "user", text }]); setSending(true); setSuggestion(null); setWrap(null);
    try {
      const { data } = await api.post("/talk", { text, local_date: new Date().toLocaleDateString("en-CA"), tz_offset: new Date().getTimezoneOffset() });
      setMessages((m) => [...m, { role: "mirro", text: data.reply, limited: !!data.talk_limited, play: data.play, card: data.card, moment: data.moment, fresh: true }]);
      if (data.wrap) setWrap({ ...data.wrap, overlay: false });
      if (data.talk_limited) toast("Out of coaching time", { duration: 8000, action: { label: "Top up", onClick: () => { window.location.href = "/topup"; } } });
      else if (data.suggestion) setSuggestion(data.suggestion);
      else if ((data.turns || 0) >= 8) setSuggestion({ label: "We've gone deep — want to land it with a quick reset?", route: "/resets", tool: "reset" });
    } catch { toast.error("Connection hiccup."); } finally { setSending(false); }
  };

  return (
    <div className="h-[calc(100dvh-4rem)] md:h-[100dvh] bg-background flex flex-col overflow-hidden">
      <Navbar />
      <div className="max-w-2xl w-full mx-auto flex-1 min-h-0 flex flex-col px-5 pt-5 pb-3">
        {nudge && <InviteNudge onClose={() => setNudge(false)} />}
        <CoachReady voice={user?.voice} compact={live || showEarlier} onTalk={() => { hearStop(); setCallOpen(true); }}
          onHear={opening ? hear : null} hearState={hearState} amplitudeRef={amplitudeRef} speakingRef={speakingRef} />

        {historyLen > 0 && !showEarlier && (
          <button onClick={() => setShowEarlier(true)} className="self-center mb-3 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="talk-show-earlier">
            Show earlier conversation
          </button>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="talk-messages">
          {shown.map((m, j) => { const i = showEarlier ? j : j + historyLen; return (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm text-sm" : "glass rounded-bl-sm"}`} data-testid={m.opening ? "talk-opening" : `talk-${m.role}`}>{m.text}{m.play && <CoachPlayCard play={m.play} autoPlay={!!m.fresh} onFinished={() => welcomeBack(i)} />}{m.card && <CoachLinkCard card={m.card} />}{m.moment && <MomentCard quote={m.moment} />}</div>
            </motion.div>
          ); })}
          {sending && <div className="flex justify-start"><div className="glass rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
          {!live && !showEarlier && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="talk-starters">
              {STARTERS.map((t, k) => (
                <button key={t} onClick={() => submit(t)} data-testid={`talk-starter-${k}`}
                  className="rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-sm text-foreground/85 hover:bg-primary/10 hover:border-primary/40 transition-colors">{t}</button>
              ))}
            </div>
          )}
          {wrap && !wrap.overlay && <TalkWrapCard wrap={wrap} />}
        </div>

        {live && myTurns >= 2 && !wrap && !sending && (
          <button onClick={() => wrapUp("chat")} disabled={wrapping} data-testid="talk-finish-btn"
            className="mb-3 w-full flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/[0.08] px-4 py-2.5 text-sm text-primary hover:bg-primary/[0.15] transition-colors">
            {wrapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Moon className="w-4 h-4" />} {wrapping ? "Setting your night…" : "Finish for tonight"}
          </button>
        )}
        {suggestion && !sending && !wrap && (
          <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={() => { markFromCoach(suggestion.tool); navigate(suggestion.route); }}
            data-testid="concierge-suggestion" className="mb-3 w-full flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/[0.07] px-4 py-3 text-left hover:bg-primary/[0.12] transition-colors">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <span className="flex-1 text-sm">{suggestion.label}</span>
            <ArrowRight className="w-4 h-4 text-primary shrink-0" />
          </motion.button>
        )}

        <form onSubmit={(e) => { e.preventDefault(); submit(input); }} className="flex items-center gap-2 pt-3 border-t border-border">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Or type to ${["onyx","echo","ash","fable"].includes(user?.voice) ? "Richard" : "Adelaida"}…`} data-testid="talk-input" className="rounded-full" />
          <Button type="submit" size="icon" className="rounded-full shrink-0" disabled={!input.trim() || sending} data-testid="talk-send"><Send className="w-4 h-4" /></Button>
        </form>
      </div>

      {callOpen && (
        <VoiceCall title={["onyx", "echo", "ash", "fable"].includes(user?.voice) ? "Richard" : "Adelaida"} voice={user?.voice || "sage"}
          openingText={opening || "Hey. I'm here — what's going on for you right now?"}
          sessionKind="talk" autoAvatar={user?.voice_visual === "avatar"}
          canUseAvatar={true}
          sendMessage={sendMessage}
          onExchange={(m) => { if (m.role === "user") callTurns.current += 1; setMessages((prev) => [...prev, m]); }}
          onEnd={() => { setCallOpen(false); if (callTurns.current > 0) { callTurns.current = 0; wrapUp("call"); } else setNudge(true); }} />
      )}
      {wrap?.overlay && <TalkWrapCard wrap={wrap} overlay onClose={() => { setWrap({ ...wrap, overlay: false }); setNudge(true); }} />}
    </div>
  );
}
